import * as React from "react"
import * as Lung from "models/lung";
import { AppContext } from "AppContext";
import { ActionTypes } from "reducer/actions";
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { FilterContext } from "models/core/filter";
import { DashChip } from "components/common/DashChips";
import ReportApi from "api/reportApi";
import * as fileHelper from "helpers/fileHelper";
import { Download, Downloading } from "@mui/icons-material";
import { LUCiDUrl, LungIndicatorDefintionsUrl, LungIndicatorYear } from "config/appUrlConfig";
import { getStateIndicatorReferences } from "api/dashboardApi";
import Loader from "pages/shared/pageloader";

enum LungIndicatorAction {
	UserClickedDownloadIndicator,
	DownloadIndicatorSuccess,
	DownloadIndicatorFail
}

type Action =
	{ type: LungIndicatorAction.UserClickedDownloadIndicator, payload: number }
	| { type: LungIndicatorAction.DownloadIndicatorSuccess }
	| { type: LungIndicatorAction.DownloadIndicatorFail }


interface State {
	indicator: number,
	downloading: boolean
}

const initialState: State = {
	indicator: 0,
	downloading: false
}



const reducer = (state: State, action: Action): State => {
	const { type } = action;

	switch (type) {
		case LungIndicatorAction.UserClickedDownloadIndicator:
			return { ...state, ...{ indicator: action.payload, downloading: true } };

		case LungIndicatorAction.DownloadIndicatorSuccess:
		case LungIndicatorAction.DownloadIndicatorFail:
			return { ...state, ...{ indicator: 0, downloading: false } };

		default:
			return state;
	}
}

let stateWideData: {
	title?: string,
	totalPatients?: string,
	hadPathDiagnosis?: string,
	hadPathDiagnosisLUCiD?: string,
	hadECOG?: string,
	hadECOGLUCiD?: string,
	hadStage?: string,
	hadStageLUCiD?: string,
	treatmentRate?: string,
	treatmentRateLUCiD?: string,
	hadSurgery?: string,
	hadSurgeryStageOneAndTwo?: string,
	hadSurgeryStageOneAndTwoLUCiD?: string,
	hadSurgOrRadFirstStageOneOrTwo?: string,
	hadSurgOrRadFirstStageOneOrTwoLUCiD?: string,
	adjuvantIVSTOperableStageTwo?: string,
	rtInoperableStageOneOrTwo?: string,
	concurrentCRTInoperableStageThree?: string,
	concurrentCRTInoperableStageThreeLUCiD?: string,
	lengthOfStay?: string,
	inHospitalMortality?: string,
	mortality30Day?: string,
	mortality90Day?: string,
	prolongedLOS?: string,
	mortality30DayRT?: string,
	mortality30DayRTLUCiD?: string,
	mortality30DayIVST?: string,
	mortality30DayIVSTLUCiD?: string,
	treatment30DaysFromDiagnosis?: string,
	surgery30DaysFromDiagnosis?: string,
	rt30DaysFromDiagnosis?: string,
	ivst30DaysFromDiagnosis?: string,
	survivalOneYearAfterSurgery?: string,
	survivalTwoYearAfterSurgery?: string,
	neoAdjOrAdjInStage2A3A?: string,
	neoAdjOrAdjInStage2A3ALUCiD?: string,
	receivedSTStage4?: string,
	receivedSTStage4LUCiD?: string
} = {};

export function LungDashboard() {
	const [localState, localDispatch] = React.useReducer(reducer, initialState)
	const { state, dispatch } = React.useContext(AppContext);


	const [showFilterChips, setFilterChips] = React.useState(true);



	const addFilter: Lung.AddFilterFunc = async (key: keyof typeof Lung.DashboardFilterKeys, value: string | string[], context?: FilterContext, labelOverride?: string) => {
		const filteredDashboard = await Lung.computeChartData(Lung.addFilter(state.page.dashboard as Lung.ILungDashboard)(key, value, context, labelOverride)); // state = what the other filter function returns 
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	const removeFilter: Lung.RemoveFilterFunc = async (key: keyof typeof Lung.DashboardFilterKeys, value?: string) => {
		const filteredDashboard = await Lung.computeChartData(Lung.removeFilter(state.page.dashboard as Lung.ILungDashboard, key, value));
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	React.useEffect(() => {
		dispatch({ type: ActionTypes.UserWantsToLoadMdtDashboard });

		const fetchStateReferenceData = async () => {
			stateWideData = await getStateIndicatorReferences("lung", +LungIndicatorYear);
		}

		const loadDashboard = async () => {
			const value = await Lung.loadLungDashboard(state.page.dashboard as Lung.ILungDashboard);
			dispatch({ type: ActionTypes.UserLoadedMdtDashboard, payload: value });
		}

		Promise.all([fetchStateReferenceData(), loadDashboard()]);

		return () => {
			Lung.closeLungDashboard();
		}
	}, []);

	React.useEffect(() => {
		if (localState.indicator == 0)
			return;

		let ids = lungChartData.level1.filteredReferralIdCsv;
		let alldata = false;


		const getLungIndicatorPatientList = async () => {
			try {
				const data = await ReportApi.getLungIndicatorPatientList(ids, alldata, localState.indicator);
				if (data.errorMessage) {
					console.error(data.errorMessage);
					localDispatch({ type: LungIndicatorAction.DownloadIndicatorFail })
				}
				localDispatch({ type: LungIndicatorAction.DownloadIndicatorSuccess })
				return fileHelper.showFile(data.bytes, data.filename, fileHelper.fileTypeToMimeType(data.filetype), "exportPatientsElem")
			} catch {
				localDispatch({ type: LungIndicatorAction.DownloadIndicatorFail })
			}
		}

		getLungIndicatorPatientList();


	}, [localState.indicator]);


	if (state.page.loading || !state.page.dashboard.chartData)
		return <Loader />


	//Create function to show/hide filter chips
	const toggleChips = () => {
		if (showFilterChips) {
			setFilterChips(false);
		} else {
			setFilterChips(true);
		}
	};

	let activeFiltersArray = [];
	let filterArray = [];
	let chips = [];

	for (const key in state.page.dashboard.filters) {
		if (Object.prototype.hasOwnProperty.call(state.page.dashboard.filters, key)) {
			activeFiltersArray.push({ "key": key, "values": state.page.dashboard.filters[key].value, "parts": state.page.dashboard.filters[key].parts });
		}
	}

	for (const key in Lung.DashboardFilterKeys) {
		if (Object.prototype.hasOwnProperty.call(Lung.DashboardFilterKeys, key)) {
			filterArray.push({ "key": key, "label": Lung.DashboardFilterKeys[key].label });
		}
	}

	activeFiltersArray.forEach(a => {
		a.values.forEach(b => {
			chips.push(<DashChip key={a.key + b}
				removeFilterFn={removeFilter}
				filterName={filterArray.find(id => id.key == a.key).label}
				filterValue={b}
				filterId={a.key}
				filterParent={(a.parts.length > 1 ? a.parts[0] : null)}
			/>
			)
		})
	})

	const lungChartData = state.page.dashboard.chartData as Lung.IChartData;

	const downloadBtn = (extractTypeId: number) => {
		const { indicator } = localState;

		if (indicator == extractTypeId)
			return <Downloading />

		return (
			<Download style={{ cursor: "pointer" }} onClick={() =>
				localDispatch({ type: LungIndicatorAction.UserClickedDownloadIndicator, payload: extractTypeId })} />
		)
	}

	const ecog = {
		numerator: 0,
		denominator: 0,
		percentage: 0
	}

	const stage = {
		numerator: 0,
		denominator: 0,
		percentage: 0
	}

	if (lungChartData.level1.totalDiagnoses > 0) {
		ecog.numerator = lungChartData.level1.totalDiagnoses - (lungChartData.level2.ecogGroup.values[lungChartData.level2.ecogGroup.labels.indexOf("Unk")] || 0);
		ecog.denominator = lungChartData.level1.totalDiagnoses
		ecog.percentage = ecog.numerator / ecog.denominator * 100

		stage.numerator = lungChartData.level1.totalDiagnoses - (lungChartData.level2.tnmStage.values[lungChartData.level2.tnmStage.labels.indexOf("Unk")] || 0);
		stage.denominator = lungChartData.level1.totalDiagnoses;
		stage.percentage = stage.numerator / stage.denominator * 100
	}


	const dashboard = <>
		<div style={{
			position: "sticky",
			top: "64px",
			left: "10px",
			zIndex: 2,
			backgroundColor: "white"
		}}>


			{(showFilterChips ? <div style={{ marginLeft: "15px", marginBottom: "15px", marginTop: "10px", width: "80%" }}>
				{chips}
			</div> : null)}
		</div>



		<div style={{
			maxWidth: 1000,
			margin: "0 auto"
		}}>

			<div style={{
				textAlign: "right",
				paddingBottom: "10px",
				paddingTop: "10px",
				position: "sticky",
				zIndex: 1,
				top: "60px",
				backgroundColor: "white"
			}}>
				<Button onClick={() => localDispatch({ type: LungIndicatorAction.UserClickedDownloadIndicator, payload: 99 })}
					style={localState.downloading && { backgroundColor: "grey", color: "white" } || {
						backgroundColor: (lungChartData.level1.totalDiagnoses > 0 ? ("#1e88e5") : ("#d3d3d3")),
						color: "white",
						alignItems: "right"
					}}>
					Download Patient Details
				</Button>
			</div>

			<TableContainer component={Paper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Indicator</TableCell>
							<TableCell align="center">My MDT(s)*</TableCell>
							<TableCell align="center">{stateWideData.title}^</TableCell>
							<TableCell align="center">LUCiD Guideline#</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>

						<TableRow>
							<TableCell colSpan={4}>
								<b>Diagnosis and Staging</b>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>NSCLC diagnoses^</TableCell>
							<TableCell align="center">{`${lungChartData.level1.totalDiagnoses}`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.totalPatients}</TableCell>
							<TableCell align="center"></TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received a pathological diagnosis</TableCell>
							<TableCell align="center">{`${lungChartData.level1.hadPathDiagnosis.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.hadPathDiagnosis.numerator}/${lungChartData.level1.hadPathDiagnosis.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadPathDiagnosis}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadPathDiagnosisLUCiD}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Performance status (ECOG) recorded</TableCell>
							<TableCell align="center">{`${ecog.percentage.toFixed(0)}%`} <br /> {`(${ecog.numerator}/${ecog.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadECOG}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadECOGLUCiD}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Stage (TNM) recorded</TableCell>
							<TableCell align="center">{`${stage.percentage.toFixed(0)}%`} <br /> {`(${stage.numerator}/${stage.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadStage}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadStageLUCiD}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell colSpan={4}>
								<b>Access</b>
							</TableCell>
						</TableRow>



						<TableRow>
							<TableCell>Received first treatment within 30 days of diagnosis</TableCell>
							<TableCell align="center">{`${lungChartData.level1.treatment30DaysFromDiagnosis.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.treatment30DaysFromDiagnosis.numerator}/${lungChartData.level1.treatment30DaysFromDiagnosis.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.treatment30DaysFromDiagnosis}</TableCell>
							<TableCell align="center">-</TableCell>

						</TableRow>

						<TableRow>
							<TableCell>Received first surgery within 30 days of diagnosis</TableCell>
							<TableCell align="center">{`${lungChartData.level1.surgery30DaysFromDiagnosis.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.surgery30DaysFromDiagnosis.numerator}/${lungChartData.level1.surgery30DaysFromDiagnosis.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.surgery30DaysFromDiagnosis}</TableCell>
							<TableCell align="center">-</TableCell>

						</TableRow>

						<TableRow>
							<TableCell>Received first radiation therapy within 30 days of diagnosis</TableCell>
							<TableCell align="center">{`${lungChartData.level1.rt30DaysFromDiagnosis.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.rt30DaysFromDiagnosis.numerator}/${lungChartData.level1.rt30DaysFromDiagnosis.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.rt30DaysFromDiagnosis}</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received first IV systemic therapy within 30 days of diagnosis</TableCell>
							<TableCell align="center">{`${lungChartData.level1.ivst30DaysFromDiagnosis.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.ivst30DaysFromDiagnosis.numerator}/${lungChartData.level1.ivst30DaysFromDiagnosis.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.ivst30DaysFromDiagnosis}</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>


						<TableRow>
							<TableCell colSpan={4}>
								<b>Treatment</b>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Treatment rate</TableCell>
							<TableCell align="center">{`${lungChartData.level1.treatmentRate.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.treatmentRate.numerator}/${lungChartData.level1.treatmentRate.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.treatmentRate}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.treatmentRateLUCiD}</TableCell>

						</TableRow>

						<TableRow>
							<TableCell>Surgery rate for all persons with NSCLC</TableCell>
							<TableCell align="center">{`${lungChartData.level1.hadSurgery.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.hadSurgery.numerator}/${lungChartData.level1.hadSurgery.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadSurgery}</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Surgery rate for stage I & II disease</TableCell>
							<TableCell align="center">{`${lungChartData.level1.hadSurgeryStageOneOrTwo.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.hadSurgeryStageOneOrTwo.numerator}/${lungChartData.level1.hadSurgeryStageOneOrTwo.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadSurgeryStageOneAndTwo}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadSurgeryStageOneAndTwoLUCiD}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Radiation therapy rate for stage I or II disease, who did not receive surgery</TableCell>
							<TableCell align="center">{`${lungChartData.level1.rtInoperableStageOneOrTwo.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.rtInoperableStageOneOrTwo.numerator}/${lungChartData.level1.rtInoperableStageOneOrTwo.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.rtInoperableStageOneOrTwo}</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Stage I or II disease who received surgery or radiation therapy as first treatment</TableCell>
							<TableCell align="center">{`${lungChartData.level1.hadSurgOrRTStageOneOrTwo.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.hadSurgOrRTStageOneOrTwo.numerator}/${lungChartData.level1.hadSurgOrRTStageOneOrTwo.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadSurgOrRadFirstStageOneOrTwo}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadSurgOrRadFirstStageOneOrTwoLUCiD}</TableCell>

						</TableRow>

						<TableRow>
							<TableCell>Neoadjuvant or adjuvant IV systemic therapy for stage IIA - IIIA disease</TableCell>
							<TableCell align="center">{`${lungChartData.level1.neoAdjOrAdjInStage2A3A.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.neoAdjOrAdjInStage2A3A.numerator}/${lungChartData.level1.neoAdjOrAdjInStage2A3A.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.neoAdjOrAdjInStage2A3A}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.neoAdjOrAdjInStage2A3ALUCiD}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Concurrent chemoradiotherapy rate in stage III disease, who did not receive surgery</TableCell>
							<TableCell align="center">{`${lungChartData.level1.concurrentCRTInoperableStageThree.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.concurrentCRTInoperableStageThree.numerator}/${lungChartData.level1.concurrentCRTInoperableStageThree.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.concurrentCRTInoperableStageThree}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.concurrentCRTInoperableStageThreeLUCiD}</TableCell>

						</TableRow>

						<TableRow>
							<TableCell>IVST for stage IV disease</TableCell>
							<TableCell align="center">{`${lungChartData.level1.receivedSTStage4.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.receivedSTStage4.numerator}/${lungChartData.level1.receivedSTStage4.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.receivedSTStage4}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.receivedSTStage4LUCiD}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell colSpan={4}>
								<b>Outcomes</b>
							</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Prolonged LOS &#8805; 12 days</TableCell>
							<TableCell align="center">{`${lungChartData.level1.prolongedLOS.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.prolongedLOS.numerator}/${lungChartData.level1.prolongedLOS.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.prolongedLOS}</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>In-Hospital surgical mortality</TableCell>
							<TableCell align="center">{`${lungChartData.level1.inHospitalMortality.percentage.toFixed(1)}%`} <br /> {`(${lungChartData.level1.inHospitalMortality.numerator}/${lungChartData.level1.inHospitalMortality.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.inHospitalMortality}</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>30-day surgical mortality</TableCell>
							<TableCell align="center">{`${lungChartData.level1.mortality30Day.percentage.toFixed(1)}%`} <br /> {`(${lungChartData.level1.mortality30Day.numerator}/${lungChartData.level1.mortality30Day.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality30Day}</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>90-day surgical mortality</TableCell>
							<TableCell align="center">{`${lungChartData.level1.mortality90Day.percentage.toFixed(1)}%`} <br /> {`(${lungChartData.level1.mortality90Day.numerator}/${lungChartData.level1.mortality90Day.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality90Day}</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>1-year surgical survival^</TableCell>
							<TableCell align="center">{`${lungChartData.level1.survivalOneYearAfterSurgery.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.survivalOneYearAfterSurgery.numerator}/${lungChartData.level1.survivalOneYearAfterSurgery.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.survivalOneYearAfterSurgery}</TableCell>
							<TableCell align="center">-</TableCell>

						</TableRow>
						<TableRow>
							<TableCell>2-year surgical survival^</TableCell>
							<TableCell align="center">{`${lungChartData.level1.survivalTwoYearAfterSurgery.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.survivalTwoYearAfterSurgery.numerator}/${lungChartData.level1.survivalTwoYearAfterSurgery.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.survivalTwoYearAfterSurgery}</TableCell>
							<TableCell align="center">-</TableCell>

						</TableRow>

						<TableRow>
							<TableCell>30-day mortality following IV systemic therapy</TableCell>
							<TableCell align="center">{`${lungChartData.level1.mortality30DayIVST.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.mortality30DayIVST.numerator}/${lungChartData.level1.mortality30DayIVST.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality30DayIVST}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality30DayIVSTLUCiD}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>30-day mortality following curative radiation therapy</TableCell>
							<TableCell align="center">{`${lungChartData.level1.mortality30DayRT.percentage.toFixed(0)}%`} <br /> {`(${lungChartData.level1.mortality30DayRT.numerator}/${lungChartData.level1.mortality30DayRT.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality30DayRT}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality30DayRTLUCiD}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell style={{ fontSize: "small" }} colSpan={6}> <i>^Patients with less than one and two years of data from their first surgery are excluded from the one and two year survival indicators respectively.</i></TableCell>
						</TableRow>

					</TableBody>
				</Table>
			</TableContainer>

			<div style={{ fontSize: "small", textAlign: "left" }}>
				<p><i>*Data based on facility of MDT</i> </p>
			</div>

			<div style={{ fontSize: "small", textAlign: "left" }}>
				<p><i>^The comparison data in the Qld report is based on a person's incidence case of the cancer. The MDT data is all diagnoses (with a diagnosis date) presented to MDT using the QOOL application</i> </p>
			</div>

			<div style={{ fontSize: "small", textAlign: "left" }}>
				<p><i>#Lung Cancer (internet-based) Delphi (LUCiD): a modified eDelphi consensus process to establish Australasian clinical quality indicators for thoracic cancer (2024). Respirology; </i> <a href={LUCiDUrl} target="_blank">http://doi.org/10.1111/resp.14812</a> </p>
			</div>

			<div style={{ fontSize: "0.875rem", textAlign: "left" }}>
				<p>This report is based on the <a href={LungIndicatorDefintionsUrl} target="_blank">Queensland Lung Cancer Quality Index 2012-2021.</a> Indicator definitions and glossary can be found in the technical appendix</p>
			</div>

		</div>

		<a id="exportPatientsElem" style={{ display: "none" }}></a>

	</>

	return (
		dashboard
	)


} 